/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Image,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '../link'
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import ItemEngravingImage from './item-engraving-image'
import {getProductLineItemUrl} from '../../utils/product-utils'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useCartItemNavigation from '../../hooks/use-cart-item-navigation'

/**
 * In the context of a cart product item variant, this component renders the item's
 * main image.
 *
 * @todo = This component will render a `SALE` badge when the qualifier is available
 * on the custom property `c_isSale`. This will need to be expanded upon to handle
 * different badge/qualifiers and property names.
 */
const ItemImage = ({imageProps, product}) => {
    const variant = useItemVariant()
    const {data: basket} = useCurrentBasket()
    const cartStyles = useMultiStyleConfig('CartStyles')
    // We find the 'small' images in the variant's image groups based on variationValues and pick the first one
    const image = findImageGroupBy(variant?.imageGroups, {
        viewType: 'large',
        selectedVariationAttributes: variant?.variationValues
    })?.images?.[0]

    const breadcrumb =
        variant?.c_breadcrumb
            ?.map((item) => item?.htmlValue)
            ?.reverse()
            ?.join('/') || ''
    const engravingDetails = product?.c_engravingDetails
        ? JSON.parse(product.c_engravingDetails)
        : null
    const productUrlFormatted = getProductLineItemUrl(basket, variant)
    const getLinkProps = (enabled, isExternal) =>
        enabled
            ? {
                  to: productUrlFormatted?.to || productUrlFormatted?.href,
                  as: Link,
                  isExternal
              }
            : {}
    const hasEngraving = variant?.c_driveRecipeID
    const isColorBarProduct =
        variant?.c_fluidConfigureImageURL &&
        variant?.c_fluidConfigureRecipeID &&
        variant?.c_fluidConfigureProductID
    const colorBarProps = getLinkProps(isColorBarProduct, true)
    const engravingProps = getLinkProps(hasEngraving, false)
    const cbImageProps = isColorBarProduct
        ? {
              minHeight: {base: '140px !important', lg: '180px !important'},
              height: {base: '140px !important', lg: '180px !important'}
          }
        : {}

    const handleClick = useCartItemNavigation({
        product,
        productUrl: productUrlFormatted?.to || productUrlFormatted?.href,
        breadcrumb,
        isExternal: isColorBarProduct,
        resetTrackingContext: true
    })

    const disabledProps = variant?.c_charitySelection
        ? {
              tabIndex: -1,
              'aria-disabled': true,
              role: 'text'
          }
        : {}
    return (
        <Box
            className="item-image-wrapper item-variant"
            as={Link}
            {...productUrlFormatted}
            display="flex"
            pointerEvents={variant?.c_charitySelection ? 'none' : 'unset'}
            {...colorBarProps}
            {...engravingProps}
            {...disabledProps}
            flexDirection="column"
            alignItems="center"
            onClick={handleClick}
        >
            <Box className="image-box-wrapper" {...cartStyles.lineItems.item.imageBox.base}>
                <Box
                    className="image-box"
                    {...cartStyles.lineItems.item.imageWrapper}
                    {...cbImageProps}
                >
                    {image && (
                        <Image
                            alt={image.alt}
                            className="product-image"
                            src={`${
                                product?.c_fluidConfigureImageURL || image.disBaseLink || image.link
                            }`}
                            ignoreFallback={true}
                            {...imageProps}
                            {...cbImageProps}
                        />
                    )}
                </Box>
            </Box>
            {product?.c_hasEngraving && (
                <ItemEngravingImage
                    engravingDetails={
                        Array.isArray(engravingDetails) && engravingDetails.length
                            ? engravingDetails
                            : []
                    }
                />
            )}
        </Box>
    )
}

ItemImage.propTypes = {
    imageProps: PropTypes.object,
    product: PropTypes.object
}

export default ItemImage
